import React, { useState } from 'react';
import {
  Box, Typography, Button, Stepper, Step, StepLabel,
  TextField, Slider, Chip, Grid, Avatar, LinearProgress,
} from '@mui/material';
import {
  PhotoCamera, CheckCircle, Schedule, AttachMoney, Gavel,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

const STEPS = ['Профиль', 'Специализация', 'Расписание', 'Прайс'];

const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const SPECIALIZATIONS = [
  'Гражданское право', 'Семейное право', 'Трудовое право',
  'Уголовное право', 'Коммерческое право', 'Налоговое право',
  'Административное право', 'Земельное право', 'Корпоративное право',
  'Интеллектуальная собственность',
];

const SX = {
  card: {
    backgroundColor: '#fff',
    borderRadius: 3,
    border: '1px solid #E8E4DE',
    p: { xs: 3, md: 5 },
    maxWidth: 640,
    mx: 'auto',
  },
  btn: { backgroundColor: '#B8956E', '&:hover': { backgroundColor: '#A07A5A' } },
  label: { fontWeight: 500, mb: 1, color: '#5A5A5A', fontSize: 14 },
};

// ── Step 1: Photo + Description ───────────────────────────────
const StepProfile = ({ data, onChange }) => (
  <Box>
    <Typography sx={SX.label}>Фото профиля</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
      <Avatar
        src={data.avatarPreview}
        sx={{ width: 72, height: 72, backgroundColor: '#E8E4DE' }}
      >
        <PhotoCamera sx={{ color: '#B8956E' }} />
      </Avatar>
      <Button component="label" variant="outlined" size="small" sx={{ borderColor: '#B8956E', color: '#B8956E' }}>
        Загрузить фото
        <input
          hidden
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) onChange('avatarFile', file, URL.createObjectURL(file));
          }}
        />
      </Button>
    </Box>

    <Typography sx={SX.label}>О себе <span style={{ color: '#f44336' }}>*</span></Typography>
    <TextField
      multiline
      rows={4}
      fullWidth
      placeholder="Расскажите о вашем опыте, образовании и специализации..."
      value={data.description}
      onChange={(e) => onChange('description', e.target.value)}
      error={data.description.length > 0 && data.description.length < 50}
      helperText={
        data.description.length > 0 && data.description.length < 50
          ? `Минимум 50 символов (введено: ${data.description.length})`
          : `${data.description.length}/500`
      }
      inputProps={{ maxLength: 500 }}
    />

    <Typography sx={{ ...SX.label, mt: 2 }}>Опыт работы (лет)</Typography>
    <Slider
      value={data.experience}
      onChange={(_, v) => onChange('experience', v)}
      min={0} max={40} step={1}
      marks={[{ value: 0, label: '0' }, { value: 10, label: '10' }, { value: 20, label: '20' }, { value: 40, label: '40+' }]}
      sx={{ color: '#B8956E', mt: 1 }}
    />
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
      {data.experience === 0 ? 'Без опыта' : `${data.experience} ${data.experience === 1 ? 'год' : data.experience < 5 ? 'года' : 'лет'}`}
    </Typography>
  </Box>
);

// ── Step 2: Specializations ────────────────────────────────────
const StepSpecializations = ({ data, onChange }) => (
  <Box>
    <Typography sx={SX.label}>
      Выберите специализации <span style={{ color: '#f44336' }}>*</span>
      <Typography component="span" variant="caption" sx={{ ml: 1, color: '#9A9A9A' }}>
        (минимум 1)
      </Typography>
    </Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
      {SPECIALIZATIONS.map((spec) => {
        const selected = data.specializations.includes(spec);
        return (
          <Chip
            key={spec}
            label={spec}
            icon={<Gavel sx={{ fontSize: 16 }} />}
            onClick={() => {
              const next = selected
                ? data.specializations.filter((s) => s !== spec)
                : [...data.specializations, spec];
              onChange('specializations', next);
            }}
            sx={{
              backgroundColor: selected ? '#B8956E' : 'transparent',
              color: selected ? '#fff' : '#5A5A5A',
              border: '1px solid',
              borderColor: selected ? '#B8956E' : '#E8E4DE',
              '&:hover': { backgroundColor: selected ? '#A07A5A' : '#F5F1EB' },
            }}
          />
        );
      })}
    </Box>
    {data.specializations.length > 0 && (
      <Typography variant="caption" sx={{ mt: 1, color: '#4CAF50', display: 'block' }}>
        ✓ Выбрано: {data.specializations.length}
      </Typography>
    )}
  </Box>
);

// ── Step 3: Schedule ──────────────────────────────────────────
const StepSchedule = ({ data, onChange }) => {
  const toggleDay = (key) => {
    const current = data.schedule[key];
    onChange('schedule', {
      ...data.schedule,
      [key]: current ? null : { start: '09:00', end: '18:00' },
    });
  };

  const updateTime = (key, field, value) => {
    onChange('schedule', {
      ...data.schedule,
      [key]: { ...data.schedule[key], [field]: value },
    });
  };

  const activeCount = Object.values(data.schedule).filter(Boolean).length;

  return (
    <Box>
      <Typography sx={SX.label}>
        Рабочие дни и часы <span style={{ color: '#f44336' }}>*</span>
        <Typography component="span" variant="caption" sx={{ ml: 1, color: '#9A9A9A' }}>
          (минимум 3 дня в неделю)
        </Typography>
      </Typography>

      {activeCount > 0 && (
        <Typography variant="caption" sx={{ color: '#4CAF50', mb: 1, display: 'block' }}>
          ✓ Выбрано дней: {activeCount}
        </Typography>
      )}

      {DAY_KEYS.map((key, idx) => {
        const active = !!data.schedule[key];
        return (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <Chip
              label={DAYS[idx]}
              onClick={() => toggleDay(key)}
              size="small"
              sx={{
                width: 48,
                backgroundColor: active ? '#B8956E' : 'transparent',
                color: active ? '#fff' : '#5A5A5A',
                border: '1px solid',
                borderColor: active ? '#B8956E' : '#E8E4DE',
                cursor: 'pointer',
              }}
            />
            {active && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  type="time"
                  size="small"
                  value={data.schedule[key].start}
                  onChange={(e) => updateTime(key, 'start', e.target.value)}
                  sx={{ width: 110 }}
                />
                <Typography variant="caption" color="text.secondary">—</Typography>
                <TextField
                  type="time"
                  size="small"
                  value={data.schedule[key].end}
                  onChange={(e) => updateTime(key, 'end', e.target.value)}
                  sx={{ width: 110 }}
                />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

// ── Step 4: Price ─────────────────────────────────────────────
const StepPrice = ({ data, onChange }) => (
  <Box>
    <Typography sx={SX.label}>
      Стоимость консультации (сум) <span style={{ color: '#f44336' }}>*</span>
    </Typography>
    <TextField
      type="number"
      fullWidth
      value={data.price}
      onChange={(e) => onChange('price', Number(e.target.value))}
      inputProps={{ min: 50000, step: 10000 }}
      helperText="Минимум: 50 000 сум. Средняя цена на платформе: 200 000 сум"
      sx={{ mb: 3 }}
    />

    <Typography sx={{ ...SX.label, mt: 1 }}>Город</Typography>
    <TextField
      fullWidth
      placeholder="Ташкент"
      value={data.location}
      onChange={(e) => onChange('location', e.target.value)}
    />

    {data.price >= 50000 && (
      <Box sx={{ mt: 3, p: 2, backgroundColor: '#F5F1EB', borderRadius: 2 }}>
        <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
          После подтверждения ваш профиль будет виден клиентам, и вы начнёте получать заявки.
        </Typography>
      </Box>
    )}
  </Box>
);

// ── Main Wizard ───────────────────────────────────────────────
const OnboardingWizard = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    description: '',
    experience: 3,
    avatarFile: null,
    avatarPreview: null,
    specializations: [],
    schedule: {},
    price: 200000,
    location: 'Ташкент',
  });

  const handleChange = (field, value, extra) => {
    setData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'avatarFile' ? { avatarPreview: extra } : {}),
    }));
  };

  const canProceed = () => {
    if (step === 0) return data.description.length >= 50;
    if (step === 1) return data.specializations.length >= 1;
    if (step === 2) return Object.values(data.schedule).filter(Boolean).length >= 3;
    if (step === 3) return data.price >= 50000;
    return false;
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('description', data.description);
      formData.append('experience', String(data.experience));
      // Основная специализация — первая из выбранных
      formData.append('specialization', data.specializations[0]);
      // Все специализации (для будущего использования)
      formData.append('languages', JSON.stringify(['uz', 'ru']));
      formData.append('schedule', JSON.stringify(data.schedule));
      formData.append('price', String(data.price));
      formData.append('location', data.location);
      if (data.avatarFile) formData.append('avatar', data.avatarFile);

      await api.put('/lawyer/profile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Профиль заполнен! Теперь вы видны клиентам.');
      onComplete();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const progress = ((step) / STEPS.length) * 100;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F5F1EB', py: 4, px: 2 }}>
      <Box sx={SX.card}>
        {/* Header */}
        <Typography
          variant="h5"
          sx={{ fontWeight: 300, letterSpacing: '0.15em', color: '#B8956E', mb: 0.5, textTransform: 'uppercase', textAlign: 'center' }}
        >
          MaslaXat
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
          Заполните профиль, чтобы начать получать клиентов
        </Typography>

        {/* Progress */}
        <LinearProgress
          variant="determinate"
          value={progress + (100 / STEPS.length)}
          sx={{ mb: 3, height: 4, borderRadius: 2, backgroundColor: '#E8E4DE', '& .MuiLinearProgress-bar': { backgroundColor: '#B8956E' } }}
        />

        {/* Stepper */}
        <Stepper activeStep={step} sx={{ mb: 4 }} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': { fontSize: 12 },
                  '& .MuiStepIcon-root.Mui-active': { color: '#B8956E' },
                  '& .MuiStepIcon-root.Mui-completed': { color: '#B8956E' },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        <Box sx={{ minHeight: 280 }}>
          {step === 0 && <StepProfile data={data} onChange={handleChange} />}
          {step === 1 && <StepSpecializations data={data} onChange={handleChange} />}
          {step === 2 && <StepSchedule data={data} onChange={handleChange} />}
          {step === 3 && <StepPrice data={data} onChange={handleChange} />}
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            onClick={() => setStep((s) => s - 1)}
            disabled={step === 0}
            sx={{ color: '#5A5A5A' }}
          >
            Назад
          </Button>

          {step < STEPS.length - 1 ? (
            <Button
              variant="contained"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canProceed()}
              sx={SX.btn}
            >
              Далее
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleFinish}
              disabled={!canProceed() || saving}
              startIcon={<CheckCircle />}
              sx={SX.btn}
            >
              {saving ? 'Сохраняем...' : 'Готово — опубликовать профиль'}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default OnboardingWizard;
